import React from 'react';
import './AmenitiesPage.css';
import Footer from '../components/Footer';

/*
  Updated AmenitiesPage to remove the "Explore" CTA and the small category pill/icon
  from each sub-amenity card (as requested).
  - sub-meta has been removed so cards display only image, title and description.
  - kept section header emoji intact; tell me if you'd like that removed too.
*/

const amenitiesData = [
  {
    key: 'wellness',
    emoji: '🌿',
    category: 'Wellness & Rejuvenation',
    intro: 'Holistic treatments and natural water features that restore body and spirit.',
    items: [
      {
        title: 'Rejuvenation Spa in the Heart of Nature',
        image: 'https://source.unsplash.com/900x600/?spa,relaxation&sig=1'
      },
      {
        title: 'Natural Spring Pond for Therapeutic Dips',
        image: 'https://source.unsplash.com/900x600/?natural-pond,spring&sig=2'
      },
      {
        title: 'Acupuncture Reflexology Walking Path',
        image: 'https://source.unsplash.com/900x600/?walking-path,zen&sig=3'
      },
      {
        title: 'Traditional Mud Bath Therapy',
        image: 'https://source.unsplash.com/900x600/?mud-bath,therapy&sig=4'
      },
      {
        title: 'Open-to-Sky Infinity Pool with Forest Views',
        image: 'https://source.unsplash.com/900x600/?infinity-pool,forest&sig=5'
      },
      {
        title: 'Private Sunbathing Decks',
        image: 'https://source.unsplash.com/900x600/?sun-deck,terrace&sig=6'
      },
      {
        title: 'Outdoor Meditation & Healing Circles',
        image: 'https://source.unsplash.com/900x600/?meditation,outdoor&sig=7'
      }
    ]
  },
  {
    key: 'nature',
    emoji: '🌸',
    category: 'Nature Immersion Experiences',
    intro: 'Deeply curated ways to reconnect with flora, fauna and seasonal gardens.',
    items: [
      {
        title: 'Butterfly Walk through Native Flora',
        image: 'https://source.unsplash.com/900x600/?butterfly,garden&sig=8'
      },
      {
        title: 'Leisure Tree Lounge Zones',
        image: 'https://source.unsplash.com/900x600/?tree-lounge,relax&sig=9'
      },
      {
        title: 'Forest Bathing Trails',
        image: 'https://source.unsplash.com/900x600/?forest-trail,trees&sig=10'
      },
      {
        title: 'Farm-to-Table Organic Gardens',
        image: 'https://source.unsplash.com/900x600/?organic-garden,farm&sig=11'
      },
      {
        title: 'Natural Water Bodies & Lotus Ponds',
        image: 'https://source.unsplash.com/900x600/?lotus-pond,water&sig=12'
      }
    ]
  },
  {
    key: 'culture',
    emoji: '🎭',
    category: 'Culture & Community',
    intro: 'Spaces and programming for music, ritual and shared experiences under the stars.',
    items: [
      {
        title: 'Open-Air Amphitheatre',
        image: 'https://source.unsplash.com/900x600/?amphitheatre,stage&sig=13'
      },
      {
        title: 'Yoga & Mindfulness Pavilion',
        image: 'https://source.unsplash.com/900x600/?yoga-pavilion,mindfulness&sig=14'
      },
      {
        title: 'Cultural Performance Arena under the Stars',
        image: 'https://source.unsplash.com/900x600/?outdoor-performance,concert&sig=15'
      }
    ]
  },
  {
    key: 'recreation',
    emoji: '⚽',
    category: 'Recreation & Fitness',
    intro: 'Active and low-impact fitness offerings that blend into the landscape.',
    items: [
      {
        title: 'Fully Equipped Sports & Fitness Centre',
        image: 'https://source.unsplash.com/900x600/?fitness-center,gym&sig=16'
      },
      {
        title: 'Nature-Inspired Cycling & Trekking Trails',
        image: 'https://source.unsplash.com/900x600/?cycling-trail,trekking&sig=17'
      },
      {
        title: 'Golf Cart Mobility across the Retreat',
        image: 'https://source.unsplash.com/900x600/?golf-cart,transport&sig=18'
      }
    ]
  },
  {
    key: 'eco',
    emoji: '🌞',
    category: 'Eco-Living & Sustainability',
    intro: 'Practical eco-innovations that reduce footprint while inviting natural harmony.',
    items: [
      {
        title: 'Solar-Powered Parking & EV Charging Stations',
        image: 'https://source.unsplash.com/900x600/?solar-panels,ev-charging&sig=19'
      },
      {
        title: 'On-Site Organic Farming',
        image: 'https://source.unsplash.com/900x600/?organic-farm,produce&sig=20'
      },
      {
        title: 'Rainwater Harvesting & Natural Cooling Architecture',
        image: 'https://source.unsplash.com/900x600/?rainwater-harvesting,sustainable-architecture&sig=21'
      }
    ]
  }
];

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function AmenitiesPage() {
  return (
    <main className="amenities-page">
      {/* Hero Section */}
      <section
        className="amenities-hero"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1508253578933-1f12a3e4b1f8?auto=format&fit=crop&w=2070&q=80')"
        }}
        aria-label="Amenities hero"
      >
        <div className="amenities-hero-overlay" />
        <div className="amenities-hero-inner">
          <span className="eyebrow">Experience</span>
          <h1 className="amenities-title">World-Class Amenities</h1>
          <p className="amenities-sub">
            Discover a curated collection of luxury experiences designed for wellness, connection, and renewal.
          </p>
        </div>
      </section>

      {/* Amenities Sections */}
      <section className="amenities-main" aria-labelledby="amenities-heading">
        <h2 id="amenities-heading" style={{ position: 'absolute', left: -9999, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}>
          Amenities
        </h2>

        {amenitiesData.map((cat) => {
          const sectionClass = `amenity-section category-${slugify(cat.key)}`;
          return (
            <section
              key={cat.key}
              className={sectionClass}
              aria-labelledby={`section-${slugify(cat.key)}-title`}
            >
              <header className="amenity-section-head">
                <div>
                  <h3 id={`section-${slugify(cat.key)}-title`} className="amenity-category">
                    {cat.category}
                  </h3>
                  <p className="amenity-intro">{cat.intro}</p>
                </div>
              </header>

              <div className="amenity-subgrid" role="list">
                {cat.items.map((item, idx) => {
                  return (
                    <article
                      role="listitem"
                      key={idx}
                      className="sub-amenity"
                      tabIndex={0}
                      aria-labelledby={`sub-${slugify(cat.key)}-${idx}-title`}
                    >
                      <img
                        src={item.image}
                        alt={`${item.title} — ${cat.category}`}
                        className="thumb"
                        loading="lazy"
                        width="900"
                        height="600"
                      />
                      <div className="sub-body">
                        <h4 id={`sub-${slugify(cat.key)}-${idx}-title`} className="sub-title">
                          {item.title}
                        </h4>
                        <p className="sub-desc">
                          {getShortDescription(cat.key, item.title)}
                        </p>
                        {/* removed the "Explore" CTA and the small category pill per request */}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </section>

      <Footer />
    </main>
  );
}

function getShortDescription(categoryKey, title) {
  const base = {
    wellness: 'Nurture cycles of restoration using natural therapies and quiet spaces.',
    nature: 'Slow, sensory experiences that highlight local ecosystems and seasonal growth.',
    culture: 'Gathering spaces and programming designed to foster connection and creativity.',
    recreation: 'Active options that blend fitness with landscape and leisure.',
    eco: 'Sustainable systems and practices integrated seamlessly across the retreat.'
  };
  return base[categoryKey] || 'A thoughtfully designed experience.';
}